import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import scrollIntoView from 'scroll-into-view';
import isNumber from 'lodash/isNumber';
import uuid from 'uuid/v4';
import { ButtonWithIcon } from '@els/els-react--button';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';

const DEFAULTS = {
  ITEM_THRESHOLD: 100,
  OFFSET_TOP: 200,
  OFFSET_BOTTOM: 50,
  THROTTLE: 100,
};

function ShowMore({
  threshold = DEFAULTS.ITEM_THRESHOLD,
  idSuffix = uuid(),
  scrollContainerSelector,
  container,
  scrollToLess,
  current,
  initial,
  total,
  labelNext,
  labelAll,
  moreHandler,
  lessHandler,
  metadata,
  documentResults,
  isLoading,
}) {
  const [isLess, setIsLess] = useState(false);
  const [isMore, setIsMore] = useState(false);
  const [label, setLabel] = useState('');
  const showMoreRef = useRef(null);
  const showLessRef = useRef(null);
  const scrollbarRef = useRef(null);

  const updateState = () => {
    const remaining = total - current;
    if (remaining >= threshold && !documentResults) {
      setLabel(labelNext || `Show next ${threshold}`);
      setIsMore(true);
    } else if (remaining > 0) {
      setLabel(labelAll || 'Show all');
      setIsMore(true);
    } else {
      setIsMore(false);
    }
  };

  // const throttledScrollHandler = throttle(scrollHandler, DEFAULTS.THROTTLE);

  const handleShowMore = () => {
    const firstItem = current + 1;
    const lastItem = total > threshold ? current + threshold : total;

    moreHandler({ firstItem, lastItem })
      ?.then(() => {
        if (!isLess) {
          setIsLess(true);
        }
      })
      ?.catch(console.error);
  };

  const handleShowLess = () => {
    const isHeatmapMode = metadata?.heatmapMode;
    const topOffset = isHeatmapMode ? 30 : 140;
    const top = isHeatmapMode ? 0 : null;

    lessHandler();
    setIsLess(false);
    // scrollbarRef.current?.removeEventListener('scroll', throttledScrollHandler);
    scrollIntoView(showMoreRef.current?.closest(scrollToLess || container), {
      behavior: 'smooth',
      align: {
        ...(isNumber(top) && { top }),
        ...(isNumber(topOffset) && { topOffset }),
      },
      time: 300,
    });
    updateState();
  };

  useEffect(() => {
    const scrollContainer = scrollContainerSelector ? document.querySelector(scrollContainerSelector) : document;
    scrollbarRef.current = scrollContainer;

    updateState();

    if (current > initial && total !== initial) {
      setIsLess(true);
    }
  }, [current, initial, scrollContainerSelector, total, updateState]);

  if (isLoading) {
    return <SimpleSpinner className="citations-results-spinner" id={`spinner-${idSuffix}`} />;
  }

  return (
    <div
      id={`show-more-${idSuffix}`}
      className={classnames({
        'u-els-display-inline-block': isLess || isMore,
      })}
      ref={showMoreRef}>
      {isLess && (
        <span className="show-less-item" ref={showLessRef}>
          <ButtonWithIcon
            data-testid="show-less-button"
            sprite="ChevronUp"
            iconRight
            className="show-less-item e2e-show-less u-els-margin-1o2 u-els-margin-left-3o4 u-els-margin-right-3o4"
            type="tertiary"
            size="small"
            onClick={handleShowLess}>
            Show Less
          </ButtonWithIcon>
        </span>
      )}
      {isMore && (
        <span className="load-more-item">
          <ButtonWithIcon
            data-testid="show-more-button"
            sprite="Plus"
            className="load-more-item e2e-show-more u-els-margin-1o2 u-els-margin-left-3o4 u-els-margin-right-3o4"
            type="tertiary"
            size="small"
            onClick={handleShowMore}>
            {label}
          </ButtonWithIcon>
        </span>
      )}
    </div>
  );
}

ShowMore.propTypes = {
  idSuffix: PropTypes.string,
  scrollContainerSelector: PropTypes.string,
  metadata: MetadataPropTypesShape,
  threshold: PropTypes.number,
  container: PropTypes.string.isRequired,
  scrollToLess: PropTypes.string,
  current: PropTypes.number.isRequired,
  initial: PropTypes.number.isRequired,
  total: PropTypes.number,
  labelNext: PropTypes.string,
  labelAll: PropTypes.string,
  moreHandler: PropTypes.func,
  lessHandler: PropTypes.func,
  documentResults: PropTypes.bool,
  isLoading: PropTypes.bool,
};

export default ShowMore;
